import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Input,
  Button,
  Heading,
  Image,
  SimpleGrid,
  VStack,
  useDisclosure,
  HStack,
  Dialog,
  Portal,
  CloseButton
} from '@chakra-ui/react';
import { LuPlus, LuImage, LuPencil, LuTrash2 } from 'react-icons/lu';
import { CONFIG_BASE_URL } from '../../core/constants';
import { COLORS } from '../../../theme';
import ImageDialog from './ImageDialog';
import ReorderCategories from './ReorderCategories';

export default function ManageImages() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState(null);

  const {
    open: isDialogOpen,
    onOpen: dialogOnOpen,
    onClose: dialogOnClose
  } = useDisclosure();

  const {
    open: isReorderOpen,
    onOpen: reorderOnOpen,
    onClose: reorderOnClose
  } = useDisclosure();

  const [dialogMode, setDialogMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);

  const {
    open: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose
  } = useDisclosure();
  const [deletingItem, setDeletingItem] = useState(null);

  useEffect(() => {
    loadImages();
  }, []);

  async function loadImages() {
    setLoading(true);
    try {
      const res = await axios.get(`${CONFIG_BASE_URL}/images.json`, {
        headers: { Accept: 'application/vnd.github.v3.raw' }
      });
      setItems(res.data || []);
      setMessage(null);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to load images' });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  const openCreate = () => {
    setDialogMode('create');
    setEditingItem(null);
    dialogOnOpen();
  };

  const openEdit = (item) => {
    setDialogMode('edit');
    setEditingItem(item);
    dialogOnOpen();
  };

  const openDelete = (item) => {
    setDeletingItem(item);
    deleteOnOpen();
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      let updatedItems = [...items];
      if (dialogMode === 'create') {
        updatedItems.push(formData);
      } else {
        const index = updatedItems.findIndex(
          (i) => i.urlPath === editingItem.urlPath
        );
        if (index !== -1) updatedItems[index] = formData;
      }

      await commitImages(updatedItems);
      setMessage({
        type: 'success',
        text: `Category ${dialogMode === 'create' ? 'added' : 'updated'} successfully`
      });
      dialogOnClose();
      setItems(updatedItems);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to save: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      const updatedItems = items.filter(
        (i) => i.urlPath !== deletingItem.urlPath
      );
      await commitImages(updatedItems);
      setMessage({ type: 'success', text: 'Category deleted' });
      setItems(updatedItems);
      deleteOnClose();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Delete failed: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReorderSave = async (updatedItems) => {
    setIsSaving(true);
    try {
      await commitImages(updatedItems);
      setItems(updatedItems);
      setMessage({
        type: 'success',
        text: 'Categories reordered successfully'
      });
      reorderOnClose();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to reorder: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  async function commitImages(newItems) {
    await axios.post(
      '/api/github/update-config',
      {
        type: 'image',
        content: JSON.stringify(newItems, null, 2)
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  const filteredItems = items
    ? items.filter((item) => {
        const searchStr = `${item.title} ${item.category}`.toLowerCase();
        return searchStr.includes(query.toLowerCase());
      })
    : [];

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="purple.500" />
        <Text mt={4} color="gray.500">
          Loading images...
        </Text>
      </Box>
    );
  }

  return (
    <Box maxW="1200px" mx="auto" p={4}>
      <Flex justify="space-between" align="center" mb={10}>
        <Box>
          <Heading size="xl">Manage Images</Heading>
          <Text color="gray.500" fontSize="sm">
            Manage your category-based image galleries
          </Text>
        </Box>
      </Flex>

      <Box mb={8}>
        <HStack gap={3}>
          <Input
            placeholder="Filter categories..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxW="400px"
            borderRadius="full"
            bg="white"
            _dark={{ bg: 'gray.800' }}
          />

          <Button
            variant="outline"
            onClick={reorderOnOpen}
            disabled={!items || items.length < 2}
          >
            Reorder Categories
          </Button>
          <Button
            colorPalette="orange"
            bg={COLORS.brand.primary}
            _hover={{ bg: COLORS.brand.hover }}
            leftIcon={<LuPlus />}
            onClick={openCreate}
          >
            Add New Category
          </Button>
        </HStack>
      </Box>

      {message && (
        <Box
          mb={6}
          p={4}
          borderRadius="xl"
          bg={message.type === 'error' ? 'red.50' : 'blue.50'}
          color={message.type === 'error' ? 'red.700' : 'blue.700'}
        >
          {message.text}
        </Box>
      )}

      {filteredItems.length === 0 ? (
        <Box
          p={10}
          textAlign="center"
          borderWidth="1px"
          borderStyle="dashed"
          borderRadius="lg"
        >
          <Text color="gray.500">
            No categories found. Click "Add New Category" to get started.
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8}>
          {filteredItems.map((item) => (
            <Box
              key={item.urlPath}
              p={6}
              borderRadius="2xl"
              borderWidth="1px"
              bg="white"
              _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
            >
              <VStack align="stretch" gap={4}>
                <Box
                  h="160px"
                  borderRadius="xl"
                  overflow="hidden"
                  bg="gray.100"
                >
                  {item.images && item.images[0] ? (
                    <Image
                      src={item.images[0]}
                      alt={item.title}
                      w="full"
                      h="full"
                      objectFit="cover"
                    />
                  ) : (
                    <Flex
                      w="full"
                      h="full"
                      align="center"
                      justify="center"
                      color="gray.300"
                    >
                      <LuImage size={48} />
                    </Flex>
                  )}
                </Box>
                <Box>
                  <Heading size="md" mb={1}>
                    {item.title}
                  </Heading>
                  <Text fontSize="sm" color="gray.500">
                    {item.category} • {item.images ? item.images.length : 0}{' '}
                    Images
                  </Text>
                </Box>
                <Flex gap={2} mt={2} justifyContent="flex-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(item)}
                    leftIcon={<LuPencil />}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorPalette="red"
                    onClick={() => openDelete(item)}
                    leftIcon={<LuTrash2 />}
                  >
                    Delete
                  </Button>
                </Flex>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Dialogs */}
      <ImageDialog
        isOpen={isDialogOpen}
        onClose={dialogOnClose}
        mode={dialogMode}
        imageData={editingItem}
        isSaving={isSaving}
        onSave={handleSave}
      />

      <ReorderCategories
        isOpen={isReorderOpen}
        onClose={reorderOnClose}
        items={items || []}
        isSaving={isSaving}
        onSave={handleReorderSave}
      />

      <Dialog.Root
        placement="center"
        motionPreset="slide-in-bottom"
        open={deleteIsOpen}
        onOpenChange={(details) => !details.open && deleteOnClose()}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Confirm Deletion</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                Are you sure you want to remove the category{' '}
                <strong>{deletingItem?.title}</strong>? This action cannot be
                undone.
              </Dialog.Body>
              <Dialog.Footer>
                <Flex justify="flex-end" gap={2} w="full">
                  <Button onClick={deleteOnClose} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    loading={isSaving}
                  >
                    Delete
                  </Button>
                </Flex>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
}
